import type { Temporal } from '@js-temporal/polyfill'
import type { DayOfWeek } from '@lucimark/shared/models/time-block'
import { dayOfWeekSchema, timeOfDaySchema } from '@lucimark/shared/models/time-block'
import z from 'zod'

/**
 * One recurring weekly window for when a screen may run **scheduled playback** on the player.
 * Outside all configured blocks, the player shows black (overlays unchanged) and does not materialize the schedule.
 * Day-of-week and clock are interpreted in the **player device wall clock**: the same offset the player reports on
 * playback heartbeats (`utcOffsetMinutes` = `-Date.getTimezoneOffset()`), i.e. the OS local timezone at evaluation time
 * (including DST transitions as the OS applies them).
 */
export const playerWeeklyAvailabilityBlockSchema = z.object({
  days: z.array(dayOfWeekSchema).min(1).max(7),
  start: timeOfDaySchema,
  end: timeOfDaySchema
}).superRefine((b, ctx) => {
  const startM = b.start.hour * 60 + b.start.minute
  const endM = b.end.hour * 60 + b.end.minute
  if (endM <= startM) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'endTimeMustBeAfterStart'
    })
  }
})

export const playerWeeklyAvailabilityScheduleSchema = z.object({
  blocks: z.array(playerWeeklyAvailabilityBlockSchema).max(32)
})

export type TPlayerWeeklyAvailabilityBlock = z.infer<typeof playerWeeklyAvailabilityBlockSchema>
export type TPlayerWeeklyAvailabilitySchedule = z.infer<typeof playerWeeklyAvailabilityScheduleSchema>

/** Normalize JSON from DB / API into a valid schedule or null when unset. */
export function parsePlayerWeeklyAvailabilitySchedule(
  raw: unknown
): TPlayerWeeklyAvailabilitySchedule | null {
  if (raw == null) {
    return null
  }
  const parsed = playerWeeklyAvailabilityScheduleSchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}

/** Temporal `dayOfWeek`: 1=Mon … 7=Sun → our `DayOfWeek`: 0=Sun … 6=Sat. */
function temporalDayToDayOfWeek(temporalDay: number): number {
  return temporalDay === 7 ? 0 : temporalDay
}

function timeOfDayToMinutes(t: { hour: number, minute: number }): number {
  return t.hour * 60 + t.minute
}

const MS_PER_MINUTE = 60_000

/** Same bounds as playback-heartbeat `utcOffsetMinutes` (minutes east of UTC). */
const UTC_OFFSET_MIN_MIN = -720
const UTC_OFFSET_MIN_MAX = 840

/**
 * Builds a fixed-offset Temporal time-zone id (`+05:30`, `-03:00`) from heartbeat-style `utcOffsetMinutes`.
 */
export function utcOffsetMinutesToTemporalTimeZoneId(utcOffsetMinutes: number): string {
  const clamped = Math.min(UTC_OFFSET_MIN_MAX, Math.max(UTC_OFFSET_MIN_MIN, utcOffsetMinutes))
  const sign = clamped >= 0 ? '+' : '-'
  const abs = Math.abs(clamped)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function instantWithinScheduleZoned(
  schedule: TPlayerWeeklyAvailabilitySchedule,
  zoned: Temporal.ZonedDateTime
): boolean {
  const dayOfWeek = temporalDayToDayOfWeek(zoned.dayOfWeek)
  const refMinutes = zoned.hour * 60 + zoned.minute

  return schedule.blocks.some((block) => {
    if (!block.days.includes(dayOfWeek as DayOfWeek)) {
      return false
    }
    const startM = timeOfDayToMinutes(block.start)
    const endM = timeOfDayToMinutes(block.end)
    if (endM <= startM) {
      return false
    }
    return refMinutes >= startM && refMinutes < endM
  })
}

/**
 * Evaluate weekly windows using the player spine convention: **local wall clock encoded as a UTC instant**
 * (see player `getLocalAsUtcInstant()`). Do not pass {@link isInstantWithinPlayerWeeklyAvailabilityAtUtcOffset}
 * a real UTC instant plus device offset for on-device playback — that double-applies when the spine already
 * uses local-as-UTC, and breaks when `getTimezoneOffset()` is wrong while `Date` local fields are right.
 */
export function isLocalWallClockInstantWithinPlayerWeeklyAvailability(
  schedule: TPlayerWeeklyAvailabilitySchedule | null,
  localWallClockAsUtc: Temporal.Instant
): boolean {
  return isInstantWithinPlayerWeeklyAvailabilityAtUtcOffset(schedule, localWallClockAsUtc, 0)
}

/**
 * When `schedule` is null or has no blocks, the screen is always “in window” (no weekly restriction).
 * Otherwise returns true iff `reference` falls inside at least one block when interpreted in the wall clock
 * defined by `utcOffsetMinutes` (same semantics as playback-heartbeat `utcOffsetMinutes`: minutes east of UTC).
 */
export function isInstantWithinPlayerWeeklyAvailabilityAtUtcOffset(
  schedule: TPlayerWeeklyAvailabilitySchedule | null,
  reference: Temporal.Instant,
  utcOffsetMinutes: number
): boolean {
  if (schedule == null || schedule.blocks.length === 0) {
    return true
  }
  if (!Number.isFinite(utcOffsetMinutes)) {
    const zoned = reference.toZonedDateTimeISO('UTC')
    return instantWithinScheduleZoned(schedule, zoned)
  }
  const tz = utcOffsetMinutesToTemporalTimeZoneId(utcOffsetMinutes)
  const zoned = reference.toZonedDateTimeISO(tz)
  return instantWithinScheduleZoned(schedule, zoned)
}

/**
 * UTC wall clock (offset 0). Prefer {@link isInstantWithinPlayerWeeklyAvailabilityAtUtcOffset} for player playback;
 * this remains useful for tests and any caller that intentionally anchors to UTC.
 */
export function isUtcInstantWithinPlayerWeeklyAvailability(
  schedule: TPlayerWeeklyAvailabilitySchedule | null,
  reference: Temporal.Instant
): boolean {
  return isInstantWithinPlayerWeeklyAvailabilityAtUtcOffset(schedule, reference, 0)
}

/** [startMs, endMs) real UTC instants, in ascending order and non-overlapping. */
export type TUtcMsInterval = [number, number]

function mergeUtcMsIntervals(intervals: TUtcMsInterval[]): TUtcMsInterval[] {
  if (intervals.length === 0) {
    return []
  }
  const sorted = [...intervals].sort((a, b) => a[0] - b[0])
  const out: TUtcMsInterval[] = [sorted[0]!]
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i]!
    const last = out[out.length - 1]!
    if (s <= last[1]) {
      last[1] = Math.max(last[1], e)
    }
    else {
      out.push([s, e])
    }
  }
  return out
}

/**
 * Weekly-schedule "in window" spans as real UTC-ms intervals within `[windowStartMs, windowEndMs)` (epic
 * #2311/#2318, T7) — used to clip the session-fill denominator to declared operating hours for fixed PDV
 * fleets. Day-of-week and clock are the block's own wall clock, offset from UTC by the fixed
 * `utcOffsetMinutes` (same convention as {@link isInstantWithinPlayerWeeklyAvailabilityAtUtcOffset} — not
 * DST-aware, matching the player's on-device offset). Returns `[]` for an empty/null schedule or an empty
 * window — callers should skip clipping entirely when the player has no schedule (P1 session-only behavior).
 */
export function playerWeeklyAvailabilityIntervalsUtcMs(
  schedule: TPlayerWeeklyAvailabilitySchedule | null,
  utcOffsetMinutes: number,
  windowStartMs: number,
  windowEndMs: number
): TUtcMsInterval[] {
  if (schedule == null || schedule.blocks.length === 0 || windowEndMs <= windowStartMs) {
    return []
  }
  const DAY_MS = 24 * 60 * 60 * 1000
  const offsetMs = Number.isFinite(utcOffsetMinutes) ? utcOffsetMinutes * 60_000 : 0
  // "Local-as-UTC" trick (see module doc): a real UTC instant plus the fixed offset, read back with
  // UTC accessors, yields the block's own wall-clock day/hour/minute directly.
  const localStart = windowStartMs + offsetMs
  const localEnd = windowEndMs + offsetMs
  const firstDayLocalMidnight = Math.floor(localStart / DAY_MS) * DAY_MS
  const lastDayLocalMidnight = Math.floor((localEnd - 1) / DAY_MS) * DAY_MS

  const intervals: TUtcMsInterval[] = []
  for (let dayLocalMidnight = firstDayLocalMidnight; dayLocalMidnight <= lastDayLocalMidnight; dayLocalMidnight += DAY_MS) {
    const dayOfWeek = new Date(dayLocalMidnight).getUTCDay() as DayOfWeek
    for (const block of schedule.blocks) {
      if (!block.days.includes(dayOfWeek)) {
        continue
      }
      const blockStartLocal = dayLocalMidnight + timeOfDayToMinutes(block.start) * MS_PER_MINUTE
      const blockEndLocal = dayLocalMidnight + timeOfDayToMinutes(block.end) * MS_PER_MINUTE
      const startUtc = Math.max(windowStartMs, blockStartLocal - offsetMs)
      const endUtc = Math.min(windowEndMs, blockEndLocal - offsetMs)
      if (startUtc < endUtc) {
        intervals.push([startUtc, endUtc])
      }
    }
  }
  return mergeUtcMsIntervals(intervals)
}

/** Intersection of two ascending, non-overlapping interval lists — real UTC-ms `[start, end)` spans. */
export function intersectUtcMsIntervals(a: readonly TUtcMsInterval[], b: readonly TUtcMsInterval[]): TUtcMsInterval[] {
  const out: TUtcMsInterval[] = []
  for (const [aStart, aEnd] of a) {
    for (const [bStart, bEnd] of b) {
      const start = Math.max(aStart, bStart)
      const end = Math.min(aEnd, bEnd)
      if (start < end) {
        out.push([start, end])
      }
    }
  }
  return mergeUtcMsIntervals(out)
}

/** Upper bound when scanning for the next in/out transition (one week). */
const MAX_TRANSITION_SCAN_MINUTES = 7 * 24 * 60

/**
 * Max delay for a single player boundary timer. Long weekend gaps must not rely on one multi-hour
 * `setTimeout` (WebView / doze may never fire it while the app stays on a blackout screen).
 */
export const PLAYER_WEEKLY_AVAILABILITY_BOUNDARY_POLL_CAP_MS = 60_000

/** Next timer delay: min(msUntilTransition, cap) so the player re-polls at least every cap interval. */
export function delayMsForNextWeeklyAvailabilityBoundaryPoll(
  msUntilTransition: number,
  capMs: number = PLAYER_WEEKLY_AVAILABILITY_BOUNDARY_POLL_CAP_MS
): number {
  if (!Number.isFinite(msUntilTransition) || msUntilTransition < 1) {
    return msUntilTransition
  }
  if (!Number.isFinite(capMs) || capMs < 1) {
    return msUntilTransition
  }
  return Math.min(msUntilTransition, capMs)
}

/**
 * Milliseconds until the next weekly-window boundary (entering or leaving allowed playback),
 * or null when there is no schedule, no blocks, or no transition within one week.
 */
/** Next boundary using {@link isLocalWallClockInstantWithinPlayerWeeklyAvailability} semantics. */
export function msUntilNextPlayerWeeklyAvailabilityTransitionLocalWallClock(
  schedule: TPlayerWeeklyAvailabilitySchedule | null,
  localWallClockAsUtc: Temporal.Instant
): number | null {
  return msUntilNextPlayerWeeklyAvailabilityTransition(schedule, localWallClockAsUtc, 0)
}

export function msUntilNextPlayerWeeklyAvailabilityTransition(
  schedule: TPlayerWeeklyAvailabilitySchedule | null,
  reference: Temporal.Instant,
  utcOffsetMinutes: number
): number | null {
  if (schedule == null || schedule.blocks.length === 0) {
    return null
  }
  const currentlyIn = isInstantWithinPlayerWeeklyAvailabilityAtUtcOffset(
    schedule,
    reference,
    utcOffsetMinutes
  )
  for (let minutes = 1; minutes <= MAX_TRANSITION_SCAN_MINUTES; minutes++) {
    const probe = reference.add({ minutes })
    const inWindow = isInstantWithinPlayerWeeklyAvailabilityAtUtcOffset(
      schedule,
      probe,
      utcOffsetMinutes
    )
    if (inWindow !== currentlyIn) {
      return minutes * MS_PER_MINUTE
    }
  }
  return null
}
